// ./src/utils/api/HighlightService.ts
//
// Highlight (Library → reusable event highlights) service. OpResult-native.
// System defaults + the account's own highlights are returned; only the account's
// account-wide highlights are editable (see the `rights` field). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the eventsmagic-api HIGHLIGHT_GROUP boundary)
// =============================================================================

export const HIGHLIGHT_GROUP_VALUES = [
  "amenity",
  "policy",
  "accessibility",
  "food_drink",
  "schedule",
  "entertainment",
  "other",
] as const;
export type HighlightGroup = (typeof HIGHLIGHT_GROUP_VALUES)[number];

// =============================================================================
// GraphQL
// =============================================================================

const HIGHLIGHT_FIELDS = `
  id
  accountId
  spaceId
  code
  name
  highlightGroup
  icon
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const HIGHLIGHT_LIST_QUERY = gql`
  query HighlightList($filter: HighlightFilter, $params: PaginationAndSorting) {
    highlightList(filter: $filter, params: $params) {
      code
      data { ${HIGHLIGHT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const HIGHLIGHT_GET_QUERY = gql`
  query HighlightGet($id: ID) {
    highlightGet(id: $id) {
      code
      data { ${HIGHLIGHT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const HIGHLIGHT_GET_MANY_QUERY = gql`
  query HighlightGetMany($ids: [ID]) {
    highlightGetMany(ids: $ids) {
      code
      data { ${HIGHLIGHT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const HIGHLIGHT_CREATE_MUTATION = gql`
  mutation HighlightCreate($params: HighlightInput) {
    highlightCreate(params: $params) {
      code
      data { ${HIGHLIGHT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const HIGHLIGHT_UPDATE_MUTATION = gql`
  mutation HighlightUpdate($where: HighlightWhereInput, $params: HighlightInput) {
    highlightUpdate(where: $where, params: $params) {
      code
      data { ${HIGHLIGHT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const HIGHLIGHT_REMOVE_MUTATION = gql`
  mutation HighlightRemove($where: HighlightWhereInput) {
    highlightRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Highlight schema)
// =============================================================================

export interface Highlight {
  id?: string;
  accountId?: string | null;
  spaceId?: string | null;
  code?: string | null;
  name?: string | null;
  highlightGroup?: string | null;
  icon?: string | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). Empty = read-only. */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface HighlightInput {
  name?: string | null;
  highlightGroup?: string | null;
  icon?: string | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface HighlightBulkInput {
  status?: number | null;
}

export interface HighlightFilter {
  id?: string[] | null;
  code?: string[] | null;
  highlightGroup?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface HighlightWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class HighlightService {
  static async list(
    filter?: HighlightFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(HIGHLIGHT_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(HIGHLIGHT_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(HIGHLIGHT_GET_MANY_QUERY, { ids });
  }

  static async create(params: HighlightInput): Promise<OpResult> {
    return ApiService.executeMutation(HIGHLIGHT_CREATE_MUTATION, { params });
  }

  static async update(where: HighlightWhereInput, params: HighlightInput): Promise<OpResult> {
    return ApiService.executeMutation(HIGHLIGHT_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(HIGHLIGHT_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status on every selected highlight in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: HighlightBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(HIGHLIGHT_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected highlight in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(HIGHLIGHT_REMOVE_MUTATION, { where: { id: ids } });
  }
}
